import { z } from 'zod'

export function createStationPayloadSchema() {
    return z.looseObject({
        order_code: z.string().optional(),
        internal_order_code: z.string().optional(),
        customer_name: z.string().optional(),
        customer_phone: z.string().optional(),
        amount: z.number().optional(),
        created_by_name: z.string().optional(),
        driver_assignee_name: z.string().optional(),
        // FD-383 — người TẠO / DUYỆT phiếu xuất kho của đơn. Rỗng khi đơn chưa
        // có PXK đã duyệt, hoặc khi template không đọc hai placeholder này
        // (server chỉ tra tên khi cần).
        stock_out_created_by_name: z.string().optional(),
        stock_out_approved_by_name: z.string().optional(),
        content: z.string().optional(),
        machine_code: z.string().optional(),
        remaining_minutes: z.number().optional(),
        product_name: z.string().optional(),
        quantity_remaining: z.number().optional(),
        so_luong: z.number().optional(),
        unit: z.string().optional(),
        // Sổ xưởng có mẻ mới (#M-16).
        ledger_name: z.string().optional(),
        writer_name: z.string().optional(),
        // M-134: loại sổ + id mẻ để máy trạm kho mở form tạo phiếu.
        ledger: z.string().optional(),
        entry_id: z.string().optional(),
    })
}

export function createNotificationEventSchema() {
    const payload = createStationPayloadSchema().nullable()
    const encodedPayload = z
        .string()
        .transform((raw, ctx): unknown => {
            try {
                return JSON.parse(raw)
            } catch {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Invalid notification JSON',
                })
                return z.NEVER
            }
        })
        .pipe(payload)
    return z.looseObject({
        type: z.string(),
        speech_template: z.string().optional(),
        order_id: z.string().optional(),
        occurred_at: z.string().optional(),
        payload: z.union([payload, encodedPayload]).optional(),
    })
}

export function createStationEventSchemas() {
    const speech = {
        speech_template: z.string().optional(),
        occurred_at: z.string().optional(),
    }
    const order = z.looseObject({
        ...createStationPayloadSchema().shape,
        ...speech,
        order_id: z.string(),
        order_code: z.string(),
        customer_name: z.string(),
        amount: z.number(),
        created_at: z.string().optional(),
    })
    return {
        station_new_order: order.extend({
            type: z.literal('new_order').optional(),
        }),
        order_start_delivery: order.extend({
            type: z.literal('in_delivery').optional(),
        }),
        order_delivery_success: order.extend({
            type: z.literal('delivered').optional(),
        }),
        order_stock_out_done: order.extend({
            type: z.literal('stock_out').optional(),
        }),
        order_delivery_failed: order.extend({
            type: z.literal('delivery_failed').optional(),
        }),
        order_payment_received: order.extend({
            type: z.literal('payment_received').optional(),
        }),
        notification: createNotificationEventSchema(),
        station_bank_in: z.looseObject({
            ...speech,
            type: z.literal('bank_in').optional(),
            amount: z.number(),
            content: z.string(),
        }),
        // Biến động bảng công việc. Chỉ cần id để màn máy trạm đối chiếu xem
        // hoạt động vừa đổi có thật sự nằm trên bảng vừa nạp lại hay không;
        // phần còn lại để hiển thị nên không bắt buộc.
        station_work_activity: z.looseObject({
            ...speech,
            work_activity_id: z.string(),
            work_activity_type: z.string().optional(),
            title: z.string().optional(),
            event: z.string().optional(),
            // Giờ của lần diễn ra sự kiện mà loa đọc lên (#M-154).
            starts_at: z.string().optional(),
        }),
        station_machine_finishing: z.looseObject({
            ...speech,
            type: z.literal('machine_finishing').optional(),
            machine_code: z.string(),
            remaining_minutes: z
                .number()
                .refine(Number.isInteger, 'Expected an integer'),
        }),
        station_stock_low: z.looseObject({
            ...speech,
            type: z.literal('stock_low').optional(),
            product_name: z.string(),
            quantity_remaining: z.number(),
            unit: z.string(),
        }),
        // Sổ xưởng vừa có mẻ mới (#M-16): loa đọc để kế toán vào lập phiếu.
        station_packaging_log: z.looseObject({
            ...speech,
            type: z.literal('packaging_log_entry').optional(),
            ledger_name: z.string(),
            writer_name: z.string().optional(),
            ledger: z.string().optional(),
            entry_id: z.string().optional(),
        }),
        station_lot_debt: z.looseObject({
            ...speech,
            type: z.literal('lot_debt').optional(),
            ma_don_noi_bo: z.string(),
            ten_san_pham: z.string(),
            so_luong: z.number(),
            don_vi: z.string(),
        }),
    }
}

export type StationNewOrderEvent = z.infer<
    ReturnType<typeof createStationEventSchemas>['station_new_order']
>
export type StationNotificationEvent = z.infer<
    ReturnType<typeof createNotificationEventSchema>
>
